import { useEffect, useMemo, useState } from 'react'
import {Link2, Building2, Check, X, Clock, Plus, Search, Loader2, Star} from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { lumi } from '@/lib/lumi'
import { useLinks, type WorkLink } from '@/hooks/useLinks'
import { useAuth } from '@/hooks/useAuth'
import { useEvaluations } from '@/hooks/useEvaluations'
import CompanyEvaluationModal from '@/components/CompanyEvaluationModal'
import { getPendingPeriods } from '@/lib/periodUtils'

interface CompanyOption {
  _id: string
  userId?: string
  creator?: string
  tradeName?: string
  commercialName?: string
  name?: string
  industry?: string
  city?: string
}

function companyDisplayName(c: CompanyOption) {
  return c.tradeName || c.commercialName || c.name || 'Empresa'
}

const STATUS_BADGE: Record<WorkLink['status'], string> = {
  pendiente: 'bg-amber-50 text-amber-700',
  aceptada: 'bg-emerald-50 text-emerald-700',
  rechazada: 'bg-red-50 text-red-700',
  finalizada: 'bg-slate-100 text-slate-600',
}

const STATUS_LABEL: Record<WorkLink['status'], string> = {
  pendiente: 'Pendiente',
  aceptada: 'Activa',
  rechazada: 'Rechazada',
  finalizada: 'Finalizada',
}

function ColaboradorVinculaciones() {
  const { user } = useAuth()
  const { links, loading, fetchByCollaborator, createLink, updateLink } = useLinks()
  const { evaluations, fetchByCollaboratorToCompanies } = useEvaluations()

  const [showRequest, setShowRequest] = useState(false)
  const [evaluatingLink, setEvaluatingLink] = useState<WorkLink | null>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<CompanyOption[]>([])
  const [searching, setSearching] = useState(false)
  const [position, setPosition] = useState('')
  const [selected, setSelected] = useState<CompanyOption | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const loadLinks = () => {
    if (user?.userId) fetchByCollaborator(user.userId)
  }

  useEffect(() => {
    if (user?.userId) {
      fetchByCollaborator(user.userId)
      fetchByCollaboratorToCompanies(user.userId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId])

  /** Ventana de 24 h para evaluar tras finalizar el vínculo */
  const withinGrace = (link: WorkLink) => {
    if (link.status !== 'finalizada') return true
    const ref = link.endDate || link.updatedAt
    if (!ref) return false
    return Date.now() - new Date(ref).getTime() <= 24 * 60 * 60 * 1000
  }

  // Periodos ya evaluados por empresa (colaborador → empresa)
  const evaluatedPeriodsByCompany = useMemo(() => {
    const map = new Map<string, Set<string>>()
    evaluations.forEach((e) => {
      if (!map.has(e.companyUserId)) map.set(e.companyUserId, new Set())
      map.get(e.companyUserId)!.add(e.period)
    })
    return map
  }, [evaluations])

  /** Calcula los periodos pendientes de evaluación para un vínculo dado. */
  const pendingPeriodsFor = (link: WorkLink): string[] => {
    const evaluatedPeriods = evaluatedPeriodsByCompany.get(link.companyUserId) || new Set<string>()
    return getPendingPeriods({
      startRef: link.startDate || link.createdAt,
      endRef: link.endDate || link.updatedAt,
      isFinalized: link.status === 'finalizada',
      evaluatedPeriods,
    })
  }

  const incoming = useMemo(
    () => links.filter((l) => l.status === 'pendiente' && l.requestedBy === 'empresa'),
    [links]
  )
  const active = useMemo(() => links.filter((l) => l.status === 'aceptada'), [links])
  const history = useMemo(
    () => links.filter((l) => ['rechazada', 'finalizada'].includes(l.status) || (l.status === 'pendiente' && l.requestedBy === 'colaborador')),
    [links]
  )

  const searchCompanies = async () => {
    setSearching(true)
    try {
      const { list } = await lumi.entities.companies.list({ sort: { createdAt: -1 } })
      const q = query.trim().toLowerCase()
      const filtered = (list as CompanyOption[]).filter((c) =>
        !q ? true : companyDisplayName(c).toLowerCase().includes(q)
      )
      setResults(filtered.slice(0, 8))
    } catch (e) {
      console.error('Error al buscar empresas:', e)
    } finally {
      setSearching(false)
    }
  }

  const handleRequest = async () => {
    if (!selected || !user?.userId) return
    setSubmitting(true)
    try {
      await createLink({
        companyUserId: selected.userId || selected.creator || selected._id,
        companyName: companyDisplayName(selected),
        collaboratorUserId: user.userId,
        collaboratorName: user.userName || user.email,
        collaboratorEmail: user.email,
        position: position.trim() || undefined,
        status: 'pendiente',
        requestedBy: 'colaborador',
        creator: user.userId,
      })
      setShowRequest(false)
      setSelected(null)
      setPosition('')
      setQuery('')
      setResults([])
      loadLinks()
    } catch (e) {
      console.error('Error al solicitar vinculación:', e)
      window.alert('No se pudo enviar la solicitud. Inténtalo de nuevo.')
    } finally {
      setSubmitting(false)
    }
  }

  const respond = async (link: WorkLink, status: 'aceptada' | 'rechazada') => {
    try {
      await updateLink(link._id, {
        status,
        startDate: status === 'aceptada' ? new Date().toISOString() : undefined,
      })
      loadLinks()
    } catch (e) {
      console.error('Error al responder vinculación:', e)
    }
  }

  return (
    <ColaboradorLayout
      title="Mis vinculaciones"
      subtitle="Gestiona tus relaciones laborales con empresas. Las vinculaciones activas habilitan evaluaciones e historial."
    >
      <div className="mb-6 flex justify-end">
        <button
          onClick={() => setShowRequest(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus size={16} /> Solicitar vinculación
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-sm text-slate-500">Cargando vinculaciones...</div>
      ) : (
        <div className="space-y-8">
          {/* Solicitudes recibidas */}
          <section>
            <h2 className="mb-3 text-sm font-semibold text-slate-700">Solicitudes recibidas</h2>
            {incoming.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
                No tienes solicitudes de vinculación pendientes.
              </p>
            ) : (
              <div className="space-y-3">
                {incoming.map((link) => (
                  <div key={link._id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white text-amber-600">
                        <Building2 size={20} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{link.companyName || 'Empresa'}</p>
                        <p className="text-xs text-slate-500">{link.position || 'Te invita a vincularte'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => respond(link, 'aceptada')}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                      >
                        <Check size={15} /> Aceptar
                      </button>
                      <button
                        onClick={() => respond(link, 'rechazada')}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                      >
                        <X size={15} /> Rechazar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Vinculaciones activas */}
          <section>
            <h2 className="mb-3 text-sm font-semibold text-slate-700">Vinculaciones activas</h2>
            {active.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
                No tienes vinculaciones activas por ahora.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {active.map((link) => {
                  const pending = pendingPeriodsFor(link)
                  return (
                    <div key={link._id} className="rounded-xl border border-slate-200 bg-white p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                          <Link2 size={20} />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{link.companyName || 'Empresa'}</p>
                          <p className="truncate text-xs text-slate-500">{link.position || 'Relación laboral activa'}</p>
                        </div>
                      </div>
                      {link.startDate ? (
                        <p className="mt-3 text-xs text-slate-400">
                          Desde {new Date(link.startDate).toLocaleDateString('es-MX')}
                        </p>
                      ) : null}
                      <div className="mt-3 border-t border-slate-100 pt-3">
                        {pending.length === 0 ? (
                          <p className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                            <Star size={13} className="fill-emerald-500 text-emerald-500" /> Ya evaluaste esta empresa al día
                          </p>
                        ) : (
                          <button
                            onClick={() => setEvaluatingLink(link)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                          >
                            <Star size={13} /> Evaluar empresa
                            {pending.length > 1 ? (
                              <span className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                                {pending.length} pendientes
                              </span>
                            ) : null}
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </section>

          {/* Historial */}
          {history.length > 0 ? (
            <section>
              <h2 className="mb-3 text-sm font-semibold text-slate-700">Historial de solicitudes</h2>
              <div className="space-y-2">
                {history.map((link) => {
                  const isFinalized = link.status === 'finalizada'
                  const pending = isFinalized ? pendingPeriodsFor(link) : []
                  return (
                    <div key={link._id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Clock size={16} className="text-slate-400" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{link.companyName || 'Empresa'}</p>
                          <p className="text-xs text-slate-400">{link.position || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isFinalized && (() => {
                          const grace = withinGrace(link)
                          if (pending.length === 0) return (
                            <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                              <Star size={12} className="fill-emerald-500 text-emerald-500" /> Evaluada
                            </span>
                          )
                          if (!grace) return (
                            <span className="text-xs text-slate-400">Plazo expirado</span>
                          )
                          return (
                            <button
                              onClick={() => setEvaluatingLink(link)}
                              className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                            >
                              <Star size={11} /> Evaluar
                              {pending.length > 1 ? ` (${pending.length})` : ''}
                            </button>
                          )
                        })()}
                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_BADGE[link.status]}`}>
                          {STATUS_LABEL[link.status]}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          ) : null}
        </div>
      )}

      {/* Modal evaluar empresa */}
      {evaluatingLink && user ? (
        <CompanyEvaluationModal
          link={evaluatingLink}
          collaboratorUserId={user.userId}
          collaboratorName={user.userName || user.email || 'Colaborador'}
          availablePeriods={pendingPeriodsFor(evaluatingLink)}
          onClose={() => setEvaluatingLink(null)}
          onSuccess={() => {
            setEvaluatingLink(null)
            if (user?.userId) fetchByCollaboratorToCompanies(user.userId)
          }}
        />
      ) : null}

      {/* Modal solicitar vinculación */}
      {showRequest ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Solicitar vinculación</h3>
              <button onClick={() => setShowRequest(false)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>

            <label className="mb-1.5 block text-sm font-medium text-slate-700">Buscar empresa</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchCompanies()}
                  placeholder="Nombre de la empresa"
                  className="w-full rounded-lg border border-slate-300 py-2.5 pl-9 pr-3 text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
              <button
                onClick={searchCompanies}
                className="rounded-lg bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800"
              >
                {searching ? <Loader2 size={16} className="animate-spin" /> : 'Buscar'}
              </button>
            </div>

            {results.length > 0 ? (
              <div className="mt-3 max-h-48 space-y-1.5 overflow-y-auto">
                {results.map((c) => (
                  <button
                    key={c._id}
                    onClick={() => setSelected(c)}
                    className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm ${
                      selected?._id === c._id ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <Building2 size={16} className="text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-800">{companyDisplayName(c)}</p>
                      <p className="text-xs text-slate-400">{[c.industry, c.city].filter(Boolean).join(' · ') || 'Empresa registrada'}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : null}

            {selected ? (
              <div className="mt-4">
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Puesto o cargo (opcional)</label>
                <input
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="Ej. Desarrollador Frontend"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
            ) : null}

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowRequest(false)}
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleRequest}
                disabled={!selected || submitting}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-amber-400 disabled:opacity-50"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
                Enviar solicitud
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ColaboradorLayout>
  )
}

export default ColaboradorVinculaciones
